import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RequireAccess, RequireAuth } from '@/auth/guards';
import { AppShell } from '@/components/layout/AppShell';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { NotFoundPage } from '@/features/PlaceholderPage';
import { AuditPage } from '@/features/audit/AuditPage';
import { DeployPage } from '@/features/deploy/DeployPage';
import { RulesetsPage } from '@/features/rulesets/RulesetsPage';
import { ProfilePage } from '@/features/settings/ProfilePage';
import { OrganizationRedirect } from '@/features/tenants/OrganizationRedirect';
import { TenantDetailPage } from '@/features/tenants/TenantDetailPage';
import { TenantsPage } from '@/features/tenants/TenantsPage';
import { ToolsPage } from '@/features/tools/ToolsPage';
import { accessRules } from './nav';


/** Route table per docs/spec/08 §1. Placeholders are replaced by feature screens in M1-T17…T24. */
export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'agents', lazy: () => import('@/features/agents/AgentsCatalogPage').then((m) => ({ Component: m.AgentsCatalogPage })) },
          { path: 'agents/new', lazy: () => import('@/features/agents/NewAgentPage').then((m) => ({ Component: m.NewAgentPage })) },
          { path: 'agents/templates/:id', lazy: () => import('@/features/agents/OwnerOverviewPage').then((m) => ({ Component: m.TemplateOverviewPage })) },
          {
            path: 'agents/templates/:id/design/:version',
            lazy: () => import('@/features/designer/DesignerPage').then((m) => ({ Component: m.TemplateDesignerPage })),
          },
          { path: 'agents/templates/:id/evals/:version', lazy: () => import('@/features/evals/EvalsPage').then((m) => ({ Component: m.TemplateEvalsPage })) },
          { path: 'agents/:id', lazy: () => import('@/features/agents/OwnerOverviewPage').then((m) => ({ Component: m.AgentOverviewPage })) },
          { path: 'agents/:id/design/:version', lazy: () => import('@/features/designer/DesignerPage').then((m) => ({ Component: m.AgentDesignerPage })) },
          { path: 'agents/:id/evals/:version', lazy: () => import('@/features/evals/EvalsPage').then((m) => ({ Component: m.AgentEvalsPage })) },
          { path: 'agents/:id/deploy', element: <DeployPage /> },
          { path: 'runs', lazy: () => import('@/features/runs/RunsPage').then((m) => ({ Component: m.RunsPage })) },
          { path: 'metrics', lazy: () => import('@/features/metrics/MetricsPage').then((m) => ({ Component: m.MetricsPage })) },
          { path: 'runs/:id', lazy: () => import('@/features/runs/RunTracePage').then((m) => ({ Component: m.RunTracePage })) },
          { path: 'rulesets', element: <RulesetsPage /> },
          { path: 'settings/profile', element: <ProfilePage /> },
          { path: 'help', lazy: () => import('@/help/HowItWorksPage').then((m) => ({ Component: m.HowItWorksPage })) },
          {
            element: <RequireAccess {...accessRules.reviewers} />,
            children: [
              { path: 'conversations', lazy: () => import('@/features/conversations/ConversationsPage').then((m) => ({ Component: m.ConversationsPage })) },
              { path: 'conversations/:id', lazy: () => import('@/features/conversations/ConversationsPage').then((m) => ({ Component: m.ConversationsPage })) },
              { path: 'review', lazy: () => import('@/features/review/ReviewQueuePage').then((m) => ({ Component: m.ReviewQueuePage })) },
              { path: 'review/:id', lazy: () => import('@/features/review/ReviewDecisionPage').then((m) => ({ Component: m.ReviewDecisionPage })) },
              { path: 'escalations', lazy: () => import('@/features/escalations/EscalationsPage').then((m) => ({ Component: m.EscalationsPage })) },
              { path: 'escalations/:id', lazy: () => import('@/features/escalations/EscalationDetailPage').then((m) => ({ Component: m.EscalationDetailPage })) },
            ],
          },
          {
            element: <RequireAccess {...accessRules.platformAdmin} />,
            children: [
              { path: 'tools', element: <ToolsPage /> },
              { path: 'tenants', element: <TenantsPage /> },
            ],
          },
          {
            // Tenant admins manage their own tenant (channels, users, settings); platform-only tabs are hidden.
            element: <RequireAccess {...accessRules.tenantAdmins} />,
            children: [
              { path: 'tenants/:id', element: <TenantDetailPage /> },
              { path: 'organization', element: <OrganizationRedirect /> },
              { path: 'audit', element: <AuditPage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
